import {
  SubjectScheduleGroupAssignment,
  SubjectScheduleGroupCreatePayload,
  SubjectScheduleGroupProjectTeachingTime,
  SubjectScheduleGroupTeachingTime,
} from 'src/app/services/models/subject-schedules/subject-schedule.model';
import {
  normalizeTeachingRangeSelection,
  parseTeachingRangeIsoDate,
  teachingRangeMatchesTime,
  toNumberOrNull,
  toNumberOrZero,
} from './subject-management-create.utils';

export interface ApprovalTeachingTimeInput {
  dayOfWeek: number | null;
  startTime: string | null;
  endTime: string | null;
}

export interface ApprovalTeacherInput {
  userId: number | null;
  scheduleSubmissionId: number | null;
  teachingRange: string;
  projectTeachingTimes: ApprovalTeachingTimeInput[];
  primaryAdvisorStudentCount?: string | number | null;
  coAdvisorStudentCount?: string | number | null;
  description?: string | null;
  showInTimetable?: boolean | null;
}

export interface SubjectApprovalBuilderInput {
  academicTermId: number | null;
  subjectId: number | null;
  curriculumTypeId: number | null;
  courseunit?: string | null;
  createdById?: number | null;
  section: string;
  teachingTypeId?: number | null;
  studyModeCode: string;
  isWorkloadSplit: boolean;
  roomName: string | null;
  studentCount: unknown;
  teachingTimes: ApprovalTeachingTimeInput[];
  teachers: ApprovalTeacherInput[];
}

function buildProjectResearchTeachingTimes(teacher: ApprovalTeacherInput): SubjectScheduleGroupProjectTeachingTime[] {
  return teacher.projectTeachingTimes
    .filter((time) => time.dayOfWeek != null && time.startTime != null && time.endTime != null)
    .map((time) => ({
      day_of_week: time.dayOfWeek as number,
      start_time: time.startTime as string,
      end_time: time.endTime as string,
    }));
}

function isApprovalTeacherReady(teacher: ApprovalTeacherInput, studyModeCode: string): boolean {
  if (teacher.userId == null) {
    return false;
  }

  return studyModeCode === 'REG'
    ? normalizeTeachingRangeSelection(teacher.teachingRange).length > 0
    : buildProjectResearchTeachingTimes(teacher).length > 0;
}

function buildRegularSubjectAssignment(
  teacher: ApprovalTeacherInput,
  section: string,
  dayOfWeek: number,
  startTime: string,
  endTime: string
): SubjectScheduleGroupAssignment | null {
  const selectedTeachingDates = Array.from(new Set(normalizeTeachingRangeSelection(teacher.teachingRange)
    .filter((range) => teachingRangeMatchesTime(range, dayOfWeek, startTime, endTime))
    .map((range) => parseTeachingRangeIsoDate(range))
    .filter((date): date is NonNullable<ReturnType<typeof parseTeachingRangeIsoDate>> => date != null)
    .map((date) => date.date)))
    .map((date) => ({ date }));

  if (teacher.userId == null || selectedTeachingDates.length === 0) {
    return null;
  }

  const assignment: SubjectScheduleGroupAssignment = {
    user_id: teacher.userId,
    section,
    selected_teaching_dates: selectedTeachingDates,
  };

  if (teacher.scheduleSubmissionId != null) {
    assignment.schedule_submission_id = teacher.scheduleSubmissionId;
  }

  return assignment;
}

function buildRegularSubjectTeachingTimes(input: SubjectApprovalBuilderInput): SubjectScheduleGroupTeachingTime[] {
  return input.teachingTimes
    .map((time): SubjectScheduleGroupTeachingTime | null => {
      if (time.dayOfWeek == null || time.startTime == null || time.endTime == null) {
        return null;
      }

      const selectedBy = input.teachers
        .map((teacher) => buildRegularSubjectAssignment(
          teacher,
          input.section,
          time.dayOfWeek as number,
          time.startTime as string,
          time.endTime as string
        ))
        .filter((assignment): assignment is SubjectScheduleGroupAssignment => assignment != null);

      return {
        day_of_week: time.dayOfWeek,
        start_time: time.startTime,
        end_time: time.endTime,
        room_name: input.roomName ?? undefined,
        selected_by: selectedBy,
      };
    })
    .filter((time): time is SubjectScheduleGroupTeachingTime => time != null);
}

function buildRegularSubjectPayload(
  input: SubjectApprovalBuilderInput
): SubjectScheduleGroupCreatePayload | null {
  const studentCount = toNumberOrNull(input.studentCount);
  const teachingTimes = buildRegularSubjectTeachingTimes(input);

  if (studentCount == null || teachingTimes.length === 0) {
    return null;
  }

  return {
    subject_schedule_group: {
      academic_term_id: input.academicTermId as number,
      subject_id: input.subjectId as number,
      student_count: studentCount,
      curriculum_type_id: input.curriculumTypeId as number,
      courseunit: input.courseunit ?? undefined,
      created_by_id: input.createdById,
      status: 'submitted',
      teaching_type_id: input.teachingTypeId ?? undefined,
      sections: [{
        section: input.section,
        is_workload_split: input.isWorkloadSplit,
      }],
      replace_slots: true,
      study_mode_code: input.studyModeCode,
      teaching_times: teachingTimes,
    },
  };
}

function buildProjectResearchAssignment(
  teacher: ApprovalTeacherInput,
  section: string
): SubjectScheduleGroupAssignment | null {
  const teachingTimes = buildProjectResearchTeachingTimes(teacher);

  if (teacher.userId == null || teachingTimes.length === 0) {
    return null;
  }

  const assignment: SubjectScheduleGroupAssignment = {
    user_id: teacher.userId,
    section,
    description: teacher.description ?? '',
    primary_advisor_student_count: toNumberOrZero(teacher.primaryAdvisorStudentCount),
    co_advisor_student_count: toNumberOrZero(teacher.coAdvisorStudentCount),
    show_in_timetable: teacher.showInTimetable === true,
    teaching_times: teachingTimes,
  };

  if (teacher.scheduleSubmissionId != null) {
    assignment.schedule_submission_id = teacher.scheduleSubmissionId;
  }

  return assignment;
}

function buildProjectResearchPayload(
  input: SubjectApprovalBuilderInput
): SubjectScheduleGroupCreatePayload | null {
  const assignments = input.teachers
    .map((teacher) => buildProjectResearchAssignment(teacher, input.section))
    .filter((assignment): assignment is SubjectScheduleGroupAssignment => assignment != null);

  if (assignments.length === 0) {
    return null;
  }

  return {
    subject_schedule_group: {
      academic_term_id: input.academicTermId as number,
      subject_id: input.subjectId as number,
      curriculum_type_id: input.curriculumTypeId as number,
      courseunit: input.courseunit ?? undefined,
      sections: [input.section],
      status: 'submitted',
      study_mode_code: input.studyModeCode,
      assignments,
    },
  };
}

export function areApprovalTeachersReady(
  teachers: ApprovalTeacherInput[],
  studyModeCode: string
): boolean {
  return teachers.length > 0 && teachers.every((teacher) => isApprovalTeacherReady(teacher, studyModeCode));
}

export function buildSubjectApprovalPayload(
  input: SubjectApprovalBuilderInput
): SubjectScheduleGroupCreatePayload | null {
  if (
    input.academicTermId == null
    || input.subjectId == null
    || input.curriculumTypeId == null
    || input.section === ''
  ) {
    return null;
  }

  return input.studyModeCode === 'REG'
    ? buildRegularSubjectPayload(input)
    : buildProjectResearchPayload(input);
}
